/**
 * The TWO hooks everything about a customer module is keyed off
 * (PROJECT-KNOWLEDGE.md §The tenant model, Mount rules). Nothing else reads `boot.modules`.
 */
import { useMemo } from "react";

import { useBootstrap } from "../api/queries/useBootstrap";
import type { NavEntry, TenantModule } from "../domain/tenantModule";
import { useUiStore } from "../store/uiStore";
import { modules } from "../tenants";

/** Registry entries the server enabled, minus the dev footer's hidden set. */
export function useEnabledModules(): TenantModule[] {
  const { data } = useBootstrap();
  const hiddenModules = useUiStore((s) => s.hiddenModules);
  const serverKeys = (data?.modules ?? []).map((m) => m.key).join(",");
  const hidden = hiddenModules.join(",");

  return useMemo(() => {
    const enabled = new Set(serverKeys ? serverKeys.split(",") : []);
    const hiddenSet = new Set(hidden ? hidden.split(",") : []);
    return modules.filter((m) => enabled.has(m.key) && !hiddenSet.has(m.key));
  }, [serverKeys, hidden]);
}

/**
 * For each enabled module, for each string the server sent in that module's `navigation[]`,
 * the declared entry. An unknown string is ignored (logged in dev); a known entry the server
 * did not send stays hidden. Permission is applied by Phase 7, at the nav.
 */
export function useModuleNavigation(): NavEntry[] {
  const { data } = useBootstrap();
  const enabled = useEnabledModules();

  return useMemo(() => {
    const entries: NavEntry[] = [];
    for (const module of enabled) {
      const sent = data?.modules.find((m) => m.key === module.key)?.navigation ?? [];
      for (const key of sent) {
        const entry = module.navigation[key];
        if (!entry) {
          if (import.meta.env.DEV) {
            console.warn(
              `[tenants] module "${module.key}" has no navigation entry for server key "${key}" — ignored.`,
            );
          }
          continue;
        }
        entries.push(entry);
      }
    }
    return entries;
  }, [enabled, data]);
}
